/**
 * What occupies the fulfillment slot on the professional's request-detail
 * page, and whether the delivered-work row offers to replace.
 *
 * Pure, and separate from the 2,700-line page, because the branch ORDER is the
 * design (spec ebdfc771) and the failures it prevents are invisible: a slot
 * that renders the wrong thing shows a professional either no way to deliver
 * at all, or a delivered document shown nowhere on the page.
 */

export type DeliveredWorkSlot = 'table' | 'upload' | 'none';

export interface SlotInputs {
  /** How many delivered documents exist (`professionalDocs.length`). */
  deliveredCount: number;
  /** Server-derived: `isAccepter && request.canUploadDocument`. */
  canUploadCertificate: boolean;
  /** Server-derived: `uploadMode` is 'window' or 'correction'. */
  isReplacingDocument: boolean;
}

/**
 * EXISTENCE FIRST, PERMISSION SECOND — and that order is the whole point.
 *
 * Keying on `isReplacingDocument` alone would be shorter and would have a
 * hole: a request in an unexpected state (upload permitted as a FIRST
 * delivery while a document already exists) would render the upload card, and
 * because the table has moved out of the documents column the delivered
 * document would then appear nowhere on the page.
 *
 * Probing the server rule suggests that combination cannot arise — 'initial'
 * requires status in_progress, the replacement modes require completed
 * (certificate-correction.ts:76-86). This function does not rely on that. An
 * invariant that is merely probable is not one to hang a page on, and the
 * degradation is free: show the document rather than hide it.
 */
export function resolveDeliveredWorkSlot(inputs: SlotInputs): DeliveredWorkSlot {
  if (inputs.deliveredCount > 0) return 'table';
  if (inputs.canUploadCertificate) return 'upload';
  return 'none';
}

/**
 * The inline upload form belongs to the FIRST delivery only. Once a document
 * exists, delivering again is a replacement, reached from the row — a standing
 * form would be a second way to do the same thing, open above the table
 * showing what it would overwrite.
 */
export function showInlineUploadForm(inputs: SlotInputs): boolean {
  return resolveDeliveredWorkSlot(inputs) === 'upload';
}

/**
 * Whether the delivered-work row offers "Reemplazar".
 *
 * Both conditions are load-bearing. `canUploadCertificate` alone would offer
 * it on a FIRST-delivery permission, where the button would be lying about
 * what it does; `isReplacingDocument` alone would offer it after the 24h
 * window closed, when the server will refuse.
 */
export function showReplaceRowAction(inputs: SlotInputs): boolean {
  return (
    resolveDeliveredWorkSlot(inputs) === 'table' &&
    inputs.canUploadCertificate &&
    inputs.isReplacingDocument
  );
}
